import React, { useEffect, useState } from "react";
import styles from "./QuestionList.module.css";
import QuestionCard from "./components/questionCard/QuestionCard";
import AddScreenshotModal from "./components/screenshotSelector/AddScreenshotModal";
import { ScreenshotDataModel } from "../../../../chatbot/customerService/components/screenshotSelector/ScreenshotSelector";
import { ImageDataModel } from "../../../../../api/responsePayload/ImagesResponse";
import { showConsoleError } from "../../../../../util/ConsoleMessage";
import { GetImages } from "../../../../../api/ImageApiCall";
import { KowledgeContentImagePatch } from "../../../../../api/KnowledgeContentApiCall";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../store/common/hooks";
import {
  addScreenshotsToConversation,
  setConversations,
  toggleSelected,
} from "../../../../../store/conversations.slice";

interface QuestionListProps {}

const QuestionList: React.FC<QuestionListProps> = ({}) => {
  const [openScreenshotSelector, setOpenScreenshotSelector] = useState(false);
  const [imageList, setImagesList] = useState<ScreenshotDataModel[]>([]);
  const [knowledgeId, setKnowlegeId] = useState<number>();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPage] = useState<number>(0);

  const dispatch = useAppDispatch();
  const conversations = useAppSelector((s) => s.conversations.items);

  useEffect(() => {
    imagesApiCall(undefined, undefined, currentPage);
  }, [currentPage]);

  const removeAllSelection = () => {
    setImagesList(
      imageList.map((val) => {
        val.isSelected = false;
        val.displayOrder = undefined;
        return val;
      })
    );
  };

  const imagesApiCall = async (
    endpoint: string | undefined = undefined,
    id: number | undefined = undefined,
    page: number | undefined = undefined
  ) => {
    try {
      const res = await GetImages(endpoint, id, page);
      if (res) {
        setImagesList(
          res.results.map((value: ImageDataModel) => {
            return {
              id: value.id,
              src: value.file,
              isSelected: false,
              name: value.name,
              description: value.description,
              category: value.category,
            };
          })
        );
        setCurrentPage(res.current_page);
        setTotalPage(res.total_pages);
      }
    } catch (e) {
      showConsoleError("API Response:Error", e);
    }
  };

  const handleNextClicked = () => {
    const nextPage = currentPage + 1;
    if (nextPage <= totalPages) {
      setCurrentPage(nextPage);
    }
  };

  const handlePrevClicked = () => {
    const prevPage = currentPage - 1;
    if (prevPage >= 1) {
      setCurrentPage(prevPage);
    }
  };
  const handleConversationSelected = (
    conversationId: number,
    checked: boolean
  ) => {
    dispatch(toggleSelected({ id: conversationId, selected: checked }));
  };

  const handleOnScreenshotSelected = (screenshot: ScreenshotDataModel) => {
    setImagesList((prev) => {
      // Determine previous state of this item
      const prevItem = prev.find((item) => item.id === screenshot.id);
      if (!prevItem) return prev;

      const wasSelected = prevItem.isSelected;
      // Toggle selection
      const toggledList = prev.map((item) =>
        item.id === screenshot.id
          ? { ...item, isSelected: !item.isSelected }
          : item
      );

      if (!wasSelected) {
        // Selecting: assign next highest order
        const maxOrder = prev
          .filter((item) => item.isSelected)
          .reduce((max, item) => Math.max(max, item.displayOrder ?? 0), 0);
        return toggledList.map((item) =>
          item.id === screenshot.id
            ? { ...item, displayOrder: maxOrder + 1 }
            : item
        );
      } else {
        // Deselecting: remove and shift down orders
        const removedOrder = prevItem.displayOrder ?? 0;
        return toggledList.map((item) => {
          if (!item.isSelected) {
            // ensure deselected item has no order
            return { ...item, displayOrder: undefined };
          }
          // for remaining selected items, shift down if above removed order
          const order = item.displayOrder ?? 0;
          return {
            ...item,
            displayOrder: order > removedOrder ? order - 1 : order,
          };
        });
      }
    });
  };

  const handleOnClickSelected = async () => {
    setOpenScreenshotSelector(false);
    if (!knowledgeId) return;
    const selectedImages = imageList
      .filter((val) => val.isSelected)
      .map((val) => {
        return {
          display_order: val.displayOrder ? val.displayOrder : 0,
          image: {
            id: val.id,
            file: val.src,
            name: "",
            description: "",
            category: 0,
            filename: "",
          },
        };
      });
    dispatch(
      addScreenshotsToConversation({
        knowledgeId: knowledgeId,
        images: selectedImages,
      })
    );
  };

  const handleOnViewSelected = (screenshot: ScreenshotDataModel) => {
    window.open(
      screenshot.src,
      "_blank",
      "toolbar=0,location=0,menubar=0,resizable=1,width=800,height=600"
    );
  };

  const handleOpenScreenshotSelection = (knowledge_id: number) => {
    setKnowlegeId(knowledge_id);
    setOpenScreenshotSelector(true);
  };

  const handleOnSaveClicked = async (knowledge_id: number) => {
    setKnowlegeId(knowledge_id);
    const convo = conversations.find(
      (conv) => conv.knowledgeId === knowledgeId
    );

    if (convo && knowledgeId) {
      KowledgeContentImagePatch(
        knowledgeId,
        convo.knowledge_content_image.map((val) => val.image.id)
      ).then(() => {
        removeAllSelection();
      });
    }
  };

  const handleOnExitEditModeClicked = (
    knowledge_id: number,
    isEditing: boolean
  ) => {
    setKnowlegeId(knowledge_id);
    if (isEditing) {
      const convo = conversations.find(
        (conv) => conv.knowledgeId === knowledgeId
      );
      const selectedImageIds = imageList
        .filter((val) => val.isSelected)
        .map((val) => val.id);
      if (convo) {
        convo.knowledge_content_image = convo.knowledge_content_image.filter(
          (img) => !selectedImageIds.includes(img.image.id)
        );
        setConversations([...conversations]);
      }
    }
    removeAllSelection();
  };

  const handleRemoveScreenshotClicked = (screenshotId: number) => {
    const convo = conversations.find(
      (conv) => conv.knowledgeId === knowledgeId
    );

    if (convo) {
      convo.knowledge_content_image = convo.knowledge_content_image.filter(
        (img) => img.image.id !== screenshotId
      );
      setConversations([...conversations]);
    }
  };

  return (
    <div className={styles["question-group-scroll-container"]}>
      {conversations.map((con, index) => (
        <QuestionCard
          key={index + con.conversationId}
          {...con}
          onSelected={handleConversationSelected}
          openScreenshotSelector={handleOpenScreenshotSelection}
          onExitEditModeClicked={handleOnExitEditModeClicked}
          onSaveClicked={handleOnSaveClicked}
          handleRemoveScreenshotClicked={handleRemoveScreenshotClicked}
        />
      ))}

      <AddScreenshotModal
        isOpen={openScreenshotSelector}
        currentPage={currentPage}
        onNextClicked={handleNextClicked}
        onPreviousClicked={handlePrevClicked}
        totalPages={totalPages}
        onClose={() => {
          setOpenScreenshotSelector(false);
        }}
        onClickSelect={handleOnScreenshotSelected}
        onSendClicked={handleOnClickSelected}
        onClickView={handleOnViewSelected}
        screenshots={imageList}
      />
    </div>
  );
};

export default QuestionList;
